import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Box, Container, Toolbar } from '@mui/material';

// project import
import HorizontalBar from 'layout/MainLayout/Drawer/HorizontalBar';
import { connect } from 'react-redux';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { openDrawer } from 'store/actions/Menu';
import Drawer from 'layout/MainLayout/Drawer';
import Footer from 'layout/MainLayout/Footer';
import TopNavigation from 'components/Resi/Header/TopNavigation';
import { Banner as VerificationBanner } from 'components/common/Banner';
import { useAuth } from 'contexts/AuthContext';
import { useResiAccess } from 'hooks/resi/useResiAccess';
import { RESI_ROUTER_NAMESPACE } from '../../utils/route-guard/ResiGuard';

const DRAWER_WIDTH = 260;
const MINI_DRAWER_WIDTH = 60;

const ResiLayout = ({ openDrawer, drawerOpen }) => {
    const theme = useTheme();
    const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
    const downLG = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const { container, miniDrawer, menuOrientation } = useConfig();
    const { pathname } = useLocation();
    const { currentOrg } = useAuth();
    const { scopes = [] } = useResiAccess();

    const gatedScope = useMemo(() => scopes.find(scope => scope?.isGated), [scopes]);

    const limitedScope = useMemo(() => scopes.find(scope => scope?.isLimited), [scopes]);

    const isGated = Boolean(gatedScope);
    const isLimited = Boolean(limitedScope) && !isGated;

    const bannerText = useMemo(() => {
        if (isLimited) {
            return 'To access and manage all available features please verify your identity.';
        }

        return '';
    }, [isLimited]);

    const boldText = useMemo(() => {
        if (isLimited) {
            return 'Verify your identity.';
        }
        if (isGated) {
            const orgName =
                gatedScope?.org?.org_name || currentOrg?.org_name || 'this property';

            return `To access ${orgName} please verify your identity.`;
        }
    }, [isLimited, isGated, gatedScope, currentOrg]);
    const isBannerDismissed = useMemo(
        () => sessionStorage.getItem('verification-banner-dismissed') === 'true',
        []
    );

    const shouldShowBanner = useMemo(
        () =>
            !pathname.includes('upload-identification') &&
            (isLimited || isGated) &&
            !isBannerDismissed,
        [isLimited, isGated, pathname, isBannerDismissed]
    );
    const handleBannerClick = () => {
        return navigate(`${RESI_ROUTER_NAMESPACE}/upload-identification`);
    };

    const handleBannerDismiss = () => {
        return sessionStorage.setItem('verification-banner-dismissed', 'true');
    };
    const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

    useEffect(() => {
        if (!miniDrawer) {
            openDrawer(!matchDownXL);
        }
    }, [matchDownXL, miniDrawer, openDrawer]);

    const leftOffset = downLG ? 0 : drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
    const isSupportPage = pathname.includes('/support');
    return (
        <Box sx={{ display: 'flex', width: '100%' }}>
            <TopNavigation isHorizontal={isHorizontal} />

            {!isHorizontal ? <Drawer /> : <HorizontalBar />}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    overflowX: 'hidden',
                    // ml: { lg: `${leftOffset}px` },
                    width: { lg: `calc(100% - ${leftOffset}px)` },
                    p: { xs: 2, sm: 3 }
                }}
            >
                <Toolbar />

                <Container
                    maxWidth={isSupportPage ? false : container ? 'xl' : false}
                    disableGutters
                    sx={{
                        position: 'relative',
                        minHeight: 'calc(100vh - 110px)',
                        display: 'flex',
                        flexDirection: 'column',

                        ...(isSupportPage && {
                            maxWidth: 'none !important',
                            width: '100%',
                            mx: 0
                        })
                    }}
                >
                    {shouldShowBanner && (
                        <VerificationBanner
                            text={bannerText}
                            boldText={boldText}
                            dismissable={isLimited ? true : false}
                            showButton={true}
                            onButtonClick={handleBannerClick}
                            backgroundColor={isGated ? '#FFE0E5' : '#009966'}
                            borderColor={isGated ? '#FF2522' : '#009966'}
                            textColor={isGated ? '#212322' : 'white'}
                            onDismiss={handleBannerDismiss}
                        />
                    )}
                    <Outlet />
                    <Footer />
                </Container>
            </Box>
        </Box>
    );
};

const mapStateToProps = ({ menu }) => ({
    drawerOpen: menu.drawerOpen
});

export default connect(mapStateToProps, { openDrawer })(ResiLayout);
